import { Injectable } from '@angular/core';
import { Patient } from '../models/patient.model';
import { Provider } from '../models/provider.model';
import { RequestService } from './request.service';
import * as moment from 'moment';
import { FamilyGroup } from '../models/family-group.model';
import * as _ from 'lodash';
import { TypeOfAttention } from '../enums/type-of-attention.enum';
import { AppPaths } from '../enums/app-paths.enum';
import { CustomRouter } from '../routers/custom-router';
import { IntegrationType } from '../enums/integration-type.enum';
import { Country } from '../models/country.model';

@Injectable({
  providedIn: 'root'
})
export class PatientService {
  public termsCallback: (() => void) | null = null;

  constructor(
    private requestService: RequestService,
    private customRouter: CustomRouter
  ) {}

  get patient(): any {
    const patient = localStorage.getItem('patient');
    return patient ? JSON.parse(patient) : null;
  }

  set patient(patient: any) {
    localStorage.setItem('patient', JSON.stringify(patient));
  }

  async getMe(): Promise<Patient> {
    const cachedPatient = this.patient;
    if (cachedPatient && typeof cachedPatient.provider?.country !== 'string') return cachedPatient;

    const patient = await this.getMeFromServer();
    if (patient) this.patient = patient;
    return patient;
  }

  async getMeFromServer(): Promise<Patient> {
    return this.requestService.get('/me');
  }

  auth() {
    return this.requestService.get('/patient/auth');
  }

  ping(patientId: string) {
    return this.requestService.post(`/patient/ping`, { patient: patientId });
  }

  update(patientId: string, updateData: Partial<Patient>) {
    return this.requestService.put('/affiliate/' + patientId, updateData);
  }

  async validateRegistration(documentNumber: string, reference: string, providerId: string) {
    return await this.requestService.get('/affiliate/' + documentNumber, {
      validator: reference,
      provider: providerId
    });
  }
  async registerAffiliate(documentNumber: string, reference: string, providerId: string) {
    return await this.requestService.get('/affiliate/' + documentNumber + '/register', {
      validator: reference,
      provider: providerId
    });
  }
  async registerIdentityPlatformPatient(documentNumber: string, reference: string, providerId: string) {
    return await this.requestService.post('/patient/identityPlatform/register', {
      documentNumber,
      validator: reference,
      provider: providerId
    });
  }
  checkValidatorChange(validator: string) {
    return this.requestService.get('/affiliate/validator/' + validator);
  }

  async getProvider(): Promise<Provider> {
    const me = await this.getMe();
    return me.provider;
  }

  async getFamilyGroup(onlyActiveMembers = false): Promise<FamilyGroup> {
    const me = await this.getMe();
    if (typeof me.familyGroup === 'string') {
      me.familyGroup = (await this.requestService.get(`/familyGroup/${me.familyGroup}`)) as FamilyGroup;
    }

    const familyGroup = me.familyGroup;
    const normalizedFamilyGroup = {
      ...familyGroup,
      members: familyGroup.members.map((member) => ({ ...member, provider: me.provider }))
    };
    me.familyGroup = normalizedFamilyGroup;
    this.patient = me;

    if (onlyActiveMembers) {
      return {
        ...normalizedFamilyGroup,
        members: _.filter(normalizedFamilyGroup.members, (member) => member.active)
      };
    }

    return normalizedFamilyGroup;
  }

  async removeFamilyGroupMember(memberId: string): Promise<void> {
    const familyGroup = await this.getFamilyGroup();
    familyGroup.members = familyGroup.members.filter((member: Patient) => member.id !== memberId);
    this.patient = { ...this.patient, familyGroup };
  }

  async hasFamilyGroup(onlyActiveMembers = false): Promise<boolean> {
    const { members } = await this.getFamilyGroup(onlyActiveMembers);
    return members?.length > 1;
  }

  async hasFamilyGroupOrProviderAllowAddMembers(onlyActiveMembers = false): Promise<boolean> {
    const [hasFamilyGroup, provider] = await Promise.all([this.hasFamilyGroup(onlyActiveMembers), this.getProvider()]);

    return hasFamilyGroup || provider.allowAddFamilyGroupMembers;
  }

  async getCallsHistory(params?: { [key: string]: any }, skip = 0, limit = 25, findAndCount = true): Promise<any> {
    const me = await this.getMe();

    const queryParams: any = {
      limit: limit,
      skip: skip,
      status: ['ENDED', 'ENDED_MANUAL', 'ENDED_PATIENT', 'ENDED_DOCTOR', 'CONNECTION_FAILED', 'CONNECTION_LOST']
    };

    queryParams.from = params?.from || moment(me.createdAt).format('DD-MM-YYYY');
    queryParams.to = params?.to || moment(Date.now()).format('DD-MM-YYYY');

    if (params?.diagnoses) {
      queryParams.diagnoses = params?.diagnoses;
    }

    if (findAndCount) {
      queryParams.findAndCount = findAndCount;
    }

    return this.requestService.get(`/patient/${me.id}/calls`, { ...queryParams, ...params });
  }

  async getFavouritedDoctors(excludeDoctorsWhoDoNotWorkOnCall = false, typeOfAttention: TypeOfAttention.ON_DEMAND | '' = ''): Promise<any> {
    const me = await this.getMe();

    return this.requestService.get(`/patient/${me.id}/doctors/`, { excludeDoctorsWhoDoNotWorkOnCall, type_of_attention: typeOfAttention });
  }

  async addDoctorToFavourites(doctorId: string) {
    const me = await this.getMe();
    return this.requestService.put(`/patient/${me.id}/doctor/${doctorId}`, {});
  }

  async removeDoctorFromFavourites(doctorId: string) {
    const me = await this.getMe();
    return this.requestService.delete(`/patient/${me.id}/doctor/${doctorId}`);
  }

  async setTyCAccepted(version: number) {
    await this.requestService.put(`/patient/termsAndConditionsAccepted`, { version: version });

    const patient = this.patient;
    if (patient) {
      patient.termsConditionsVersionAccepted = version;
      this.patient = patient;
    }
  }

  hasShortFlow(): boolean {
    return localStorage.getItem('isShortFlow') === 'true';
  }

  async addMemberToFamilyGroup(memberData: any): Promise<any> {
    const me = await this.getMe();
    return this.requestService.post(`/patient/${me.id}/addMemberFamilyGroup`, memberData);
  }

  resetPassword(email: string): any {
    return this.requestService.post('/patient/resetPassword', { email });
  }

  async isOwner(patient?: Patient): Promise<boolean> {
    if (patient) return patient.owner || patient.titular;

    await this.getMe();
    return this.patient.owner || this.patient.titular;
  }

  async isOwnerAndIsNotAlone(onlyActiveMembers = false): Promise<boolean> {
    const [isOwner, isNotAlone] = await Promise.all([this.isOwner(), this.hasFamilyGroup(onlyActiveMembers)]);
    return isOwner && isNotAlone;
  }

  async getCheckPatientFamilyGroup(patientId: string, documentNumber: string, providerId: string): Promise<Patient> {
    return this.requestService.get(`/patient/${patientId}/checkFamilyMember/${providerId}/${documentNumber}`);
  }

  sendMedicalRecordsEmail(patientId: string, email: string): Promise<void> {
    return this.requestService.post(`/email/patient/${patientId}/medicalRecords`, { email });
  }

  async getFamilyGroupOwner(): Promise<Patient | undefined> {
    const familyGroup = await this.getFamilyGroup();
    return familyGroup?.members.find((member) => this.isOwner(member));
  }

  async checkTermsAndConditionsAccepted(callback?: () => void): Promise<void> {
    const patient = await this.getMe();
    if (_.isEmpty(patient)) {
      this.customRouter.navigate([`/${AppPaths.AUTH}`]);
      return;
    }

    const patientAcceptedTermsVersion = patient.termsConditionsVersionAccepted;
    const providerTermsVersion = patient.provider.termsConditionsVersion;
    const providerUpdatedTerms = Number(providerTermsVersion) !== Number(patientAcceptedTermsVersion);

    if (!providerUpdatedTerms) {
      if (callback) {
        callback();
      } else {
        this.customRouter.navigate([AppPaths.WARNING]);
      }
    } else {
      if (callback) {
        this.termsCallback = callback;
      }
      this.customRouter.navigate([AppPaths.TERMS_AND_CONDITIONS]);
    }
  }

  executeTermsCallback(): void {
    if (this.termsCallback) {
      this.termsCallback();
      this.termsCallback = null;
    }
  }

  sendDataRequestEmail(body: Record<string, string>): Promise<void> {
    return this.requestService.post(`/email/patient/dataRequest`, body);
  }

  async getRequiredFields(): Promise<string[]> {
    const patient = await this.getMe();
    const fields = ['birthDate', 'mobilePhone', 'gender'];

    if (_.isEmpty(patient.email)) {
      fields.push('email');
    } else if (patient.provider.serviceObjectName === IntegrationType.PROVIDER_TOKEN_SERVICE) {
      fields.push('emailLAD');
    }

    const needsValidateName = sessionStorage.getItem('needsValidateName') === 'true';
    if (needsValidateName) fields.push('name');

    if ((patient.provider.country as Country)?.countryCode === 'BR') {
      fields.push('documentTypeLAD');
      fields.push('documentNumberLAD');
    }

    return fields;
  }

  async getMissingRequiredFields(): Promise<Partial<Patient>> {
    const requiredFields = await this.getRequiredFields();
    const patient = (await this.getPreselectedCaller()) ?? this.patient;
    const missingFields: Partial<Patient> = {};

    for (const field of requiredFields) {
      if (field === 'emailLAD') {
        // The user has to have email or emailLAD
        if (!patient[field] && !patient['email']) {
          if (!patient[field]) missingFields[field] = patient[field];
          else if (!patient['email']) missingFields['email'] = patient['email'];
        } else continue;
      }

      if (patient[field] === null || patient[field] === undefined || patient[field] === '')
        missingFields[field as keyof Patient] = patient[field];
    }

    return missingFields;
  }

  async getPreselectedCaller(me?: Patient): Promise<Patient | null> {
    const defaultCredentialNumber = localStorage.getItem('defaultCredentialNumber');
    if (!defaultCredentialNumber) return null;

    let caller = null;
    me = me || (await this.getMe());
    if (me.credential === defaultCredentialNumber) caller = me;
    else {
      const familyGroup = await this.getFamilyGroup(true);
      caller = familyGroup?.members?.find((member) => member.credential === defaultCredentialNumber) ?? null;
    }
    return caller;
  }

  isAgeAtLeast(patient: Patient, years: number): boolean {
    if (typeof patient?.birthDate === 'number') {
      const age = moment().diff(moment.unix(patient.birthDate), 'years');
      return age >= years;
    }
    return false;
  }

  getCaller(): Patient | null {
    const callData = localStorage.getItem('call_data');
    return callData ? JSON.parse(callData).caller : null;
  }
}
